import type { AnimalConfig, Species } from "../../config/types";
import type { Rng } from "../../core/rng";
import { distance, type Agent, type RiderView } from "./types";

export type AnimalState = "idle" | "wander" | "bolt" | "hidden";

export interface Animal extends Agent {
    species: Species;
    state: AnimalState;
    timer: number;
    dirX: number;
}

const WANDER_S = [2, 5];
const IDLE_S = [2, 6];
const BOLT_S = 3;
const MARMOT_S = [1, 3];

const between = (rng: Rng, [min, max]: number[]) => min + rng() * (max - min);

export function createAnimal(species: Species, x: number, y: number): Animal {
    return { x, y, species, pose: species, flipX: false, state: "idle", timer: 1, dirX: 1 };
}

export function canHitAnimal(animal: Animal): boolean {
    return animal.state !== "hidden";
}

function stepMarmot(a: Animal, rng: Rng): Animal {
    if (a.timer > 0) return a;
    return { ...a, state: a.state === "hidden" ? "idle" : "hidden", timer: between(rng, MARMOT_S) };
}

export function stepAnimal(a: Animal, rider: RiderView, dt: number, cfg: AnimalConfig, rng: Rng): Animal {
    const s = { ...a, timer: Math.max(0, a.timer - dt) };
    if (s.species === "marmot") return stepMarmot(s, rng);

    if (s.state !== "bolt" && cfg.boltSpeed > 0 && distance(s, rider) < cfg.boltM) {
        return { ...s, state: "bolt", timer: BOLT_S, dirX: s.x >= rider.x ? 1 : -1 };
    }

    const speed = s.state === "bolt" ? cfg.boltSpeed : s.state === "wander" ? cfg.speed : 0;
    const moved = { ...s, x: s.x + s.dirX * speed * dt, flipX: s.dirX > 0 };
    if (s.timer > 0) return moved;

    if (s.state === "idle") return { ...moved, state: "wander", timer: between(rng, WANDER_S), dirX: rng() < 0.5 ? -1 : 1 };
    return { ...moved, state: "idle", timer: between(rng, IDLE_S) };
}
